<template>
  <div>
    <b-overlay :show="loading" rounded="sm" no-fade>
      <b-card class="mt-2">
        <b-row class="mb-2">
          <table class="table">
            <thead>
              <tr>
                <th
                  scope="col"
                  v-if="!showReviewDetail"
                  class="bg-primary text-white align-middle"
                  style="font-size: 1.2em;"
                >
                  Create Questionnaire
                </th>
                <th
                  scope="col"
                  v-else
                  class="bg-primary text-white align-middle"
                >
                  {{ questionnaire.title }}
                </th>

                <th
                  v-if="showReviewDetail"
                  scope="col"
                  class="bg-primary text-white d-flex justify-content-end align-middle"
                >
                  <b-button
                    v-b-tooltip.hover
                    :title="
                      questions.length == 0
                        ? 'Please add at least one question to the table'
                        : ''
                    "
                    :disabled="questions.length == 0"
                    variant="success"
                    class="animate__animated animate__fadeIn m-0 "
                    style="background-color:white;color:#003b49"
                    size="sm"
                    @click="saveButton()"
                  >
                    Update Questionnaire
                  </b-button>
                </th>
              </tr>
            </thead>
          </table>
        </b-row>

        <!-- Welcome Card for New Questionnaire -->
        <b-card
          v-if="!showReviewDetail"
          class="welcome-card shadow-lg border-0 animate__animated animate__fadeInUp"
        >
          <div class="text-center py-4">
            <div class="mb-4">
              <feather-icon
                icon="FileTextIcon"
                size="64"
                class="text-primary mb-3"
              />
              <h3 class="text-primary font-weight-bold">
                Create New Questionnaire
              </h3>
              <p class="text-muted mb-4">
                Start building your review by giving it a descriptive title. You
                can add questions and organize them into groups once your
                questionnaire is created.
              </p>
            </div>

            <validation-observer ref="questionaireRules1">
              <b-row class="justify-content-center">
                <b-col cols="12" md="8" lg="6">
                  <validation-provider
                    #default="{ errors }"
                    name="questionnaire"
                    rules="required|min:5"
                  >
                    <b-form-group>
                      <b-input-group size="lg">
                        <b-input-group-prepend>
                          <b-input-group-text>
                            <feather-icon icon="EditIcon" size="18" />
                          </b-input-group-text>
                        </b-input-group-prepend>
                        <b-form-input
                          id="questionnaire"
                          v-model.lazy="questionnaire.title"
                          type="text"
                          placeholder="Enter your questionnaire title..."
                          :state="questionnaireNameState"
                          aria-describedby="input-live-feedback"
                          class="text-center"
                        ></b-form-input>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                      <b-form-invalid-feedback id="input-live-feedback">
                        Please enter at least 5 characters for the title
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-row>

              <!-- Role visibility selections -->
              <b-row class="justify-content-center mt-2">
                <b-col cols="12" md="8" lg="6">
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-group label="Hide Scores From" label-for="hideScoresFrom">
                        <v-select
                          id="hideScoresFrom"
                          v-model="hideScoresFrom"
                          :options="roles"
                          label="name"
                          :reduce="role => role.id"
                          multiple
                          :loading="rolesLoading"
                          placeholder="Select roles"
                          :clearable="true"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group label="Hide Comments From" label-for="hideCommentsFrom">
                        <v-select
                          id="hideCommentsFrom"
                          v-model="hideCommentsFrom"
                          :options="roles"
                          label="name"
                          :reduce="role => role.id"
                          multiple
                          :loading="rolesLoading"
                          placeholder="Select roles"
                          :clearable="true"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="12">
                      <b-form-group
                        label="Roles Who Can See All Reviews"
                        label-for="visibleToRoles"
                        description="Selected roles can see every review for this questionnaire. Admins and Regional Managers always see all. Line managers always see their staff reviews."
                      >
                        <v-select
                          id="visibleToRoles"
                          v-model="visibleToRoles"
                          :options="roles"
                          label="name"
                          :reduce="role => role.name"
                          multiple
                          :loading="rolesLoading"
                          placeholder="Select roles..."
                          :clearable="true"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group class="mb-0">
                    <b-form-checkbox
                      v-model="questionnaire.editableByReviewee"
                      switch
                      class="text-white"
                    >
                      Editable by reviewee
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Follow-up Config Section -->
              <b-row class="justify-content-center mt-3">
                <b-col cols="12" md="8" lg="6">
                  <b-card class="border">
                    <h6 class="mb-1">Follow-up Reviews</h6>
                    <b-form-checkbox
                      v-model="followUpConfig.enabled"
                      switch
                      @change="onFollowUpEnabledChange"
                    >
                      Enable follow-up reviews
                    </b-form-checkbox>
                    <div v-if="followUpConfig.enabled" class="mt-1 pl-2">
                      <b-form-checkbox v-model="followUp6Month" @change="syncFollowUpIntervals">
                        6-Month follow-up
                      </b-form-checkbox>
                      <b-form-checkbox v-model="followUp1Year" @change="syncFollowUpIntervals" class="mt-50">
                        1-Year follow-up
                      </b-form-checkbox>
                    </div>
                  </b-card>
                </b-col>
              </b-row>

              <b-row class="justify-content-center mt-3">
                <b-col cols="auto">
                  <b-button
                    size="lg"
                    variant="primary"
                    :disabled="!questionnaireNameState"
                    @click="createQuestionnaire()"
                    class="px-5 animate__animated animate__pulse animate__infinite"
                    :class="{
                      'animate__pulse animate__infinite': questionnaireNameState,
                    }"
                  >
                    <feather-icon icon="PlusIcon" size="18" class="mr-1" />
                    Create Questionnaire
                  </b-button>
                </b-col>
              </b-row>
            </validation-observer>
          </div>
        </b-card>

        <!-- <b-row>
          <b-col>
            <hr />
          </b-col>
        </b-row> -->

        <validation-observer ref="questionaireRules2">
          <b-row
            v-if="showReviewDetail"
            class="animate__animated animate__fadeInUp"
          >
            <b-col cols="12" xl="12" md="12" sm="12">
              <!-- 1. Select or Add Group -->
              <h5 class="font-weight-bold mb-2">1. Select or Add Group</h5>
              <b-row>
                <!-- Groups -->
                <b-col cols="12" xl="3" md="3" sm="12">
                  <b-form-group label="Groups" label-for="groups">
                    <div class="d-flex align-items-center">
                      <validation-provider
                        #default="{ errors }"
                        name="group"
                        rules="required"
                        class="flex-grow-1"
                      >
                        <v-select
                          id="group"
                          v-model="questionData.group"
                          :taggable="false"
                          :autoscroll="true"
                          maxHeight="50"
                          :create-option="
                            (temp) => ({ id: 0, groupName: temp })
                          "
                          :loading="groupSelectBoxLoading"
                          @option:created="openAddGroupModal"
                          @option:selected="selectedGroupValue"
                          :reduce="(val) => val"
                          :options="groupData"
                          label="groupName"
                          :clearable="true"
                        >
                          <template #option="{ groupName, score }">
                            <span
                              >{{ groupName
                              }}<span
                                v-if="score !== undefined && score !== null"
                              >
                                (Score: {{ score }})</span
                              ></span
                            >
                          </template>
                          <template #selected-option="{ groupName, score }">
                            <span
                              >{{ groupName
                              }}<span
                                v-if="score !== undefined && score !== null"
                              >
                                (Score: {{ score }})</span
                              ></span
                            >
                          </template>
                          <template
                            #no-options="{ search, searching, loading }"
                          >
                            <div
                              class="d-flex align-items-center justify-content-between p-2"
                            >
                              <span>No options found</span>
                              <b-button
                                v-if="search"
                                size="sm"
                                variant="primary"
                                @click="openAddGroupModal(search)"
                              >
                                Add Group Name
                              </b-button>
                            </div>
                          </template>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                      <b-button
                        v-if="questionData.group"
                        size="sm"
                        variant="outline-info"
                        class="ml-1"
                        @click="openEditGroupModal(questionData.group)"
                        v-b-tooltip.hover
                        title="Edit Group"
                      >
                        <feather-icon icon="Edit3Icon" size="16" />
                      </b-button>
                      <b-button
                        v-if="
                          questionData.group &&
                            canDeleteGroup(questionData.group)
                        "
                        size="sm"
                        variant="outline-danger"
                        class="ml-1"
                        @click="deleteGroup(questionData.group)"
                        v-b-tooltip.hover
                        title="Delete Group"
                      >
                        <feather-icon icon="Trash2Icon" size="16" />
                      </b-button>
                    </div>
                  </b-form-group>
                </b-col>
                <!-- Question Type -->
              </b-row>

              <!-- 2. Enter Question Details -->
              <h5 class="font-weight-bold mt-4 mb-2">
                2. Enter Question Details
              </h5>
              <b-row class="mb-2">
                <!-- Question -->
                <b-col cols="12" xl="6" md="6" sm="12">
                  <b-form-group label="Question" label-for="Question">
                    <validation-provider
                      #default="{ errors }"
                      name="Question"
                      rules="required|min:5"
                    >
                      <b-form-input
                        id="title"
                        v-model.lazy="questionData.title"
                        type="text"
                        placeholder="Add Question"
                        :state="titleState"
                        aria-describedby="input-live-feedback"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Subtitle Explain - Only for Answer type questions -->
                <b-col cols="12" xl="6" md="6" sm="12">
                  <b-form-group
                    label="Subtitle (Optional)"
                    label-for="subtitle"
                  >
                    <b-form-input
                      id="subtitle"
                      v-model="questionData.subtitle"
                      placeholder="Add explanation for this question (optional)"
                      rows="2"
                      no-resize
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="12" sm="12" md="3" xl="3">
                  <b-form-group label="Question Type" label-for="questionType">
                    <validation-provider name="type" #default="{ errors }">
                      <v-select
                        id="type"
                        v-model="questionData.type"
                        :options="questionTypes"
                        :reduce="(val) => val"
                        :filterable="false"
                        label="name"
                        :clearable="false"
                        input-id="QuestionType"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" xl="3" md="3" sm="12">
                  <b-form-group
                    label="Max Score"
                    label-for="maxScore"
                    v-if="questionData.type.status === 'A'"
                  >
                    <validation-provider #default="{ errors }" name="maxScore">
                      <b-form-input
                        id="maxScore"
                        v-model.number="questionData.maxScore"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter max score (0-100)"
                        :state="maxScoreState"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                      <b-form-invalid-feedback>
                        Max score must be between 0-100 and greater than min
                        score
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col cols="12" xl="3" md="3" sm="12">
                  <b-form-group
                    label="Passing Score"
                    label-for="passingScore"
                    v-if="questionData.type.status === 'A'"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="passingScore"
                    >
                      <b-form-input
                        id="passingScore"
                        v-model.number="questionData.passingScore"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter passing score (1-100)"
                        :state="passingScoreState"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                      <b-form-invalid-feedback>
                        Passing score must be between min and max scores
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Button -->
                <b-col
                  cols="12"
                  xl="3"
                  md="3"
                  sm="12"
                  class="d-flex align-items-start mt-2"
                >
                  <b-button
                    block
                    variant="outline-success"
                    :disabled="questionData.title === '' ? true : false"
                    @click="addQuestion()"
                    >Add New Question +</b-button
                  >
                </b-col>
              </b-row>

              <!-- 3. Review Preview -->
              <h5 class="font-weight-bold mt-4 mb-2">3. Review Preview</h5>
              <b-row class="mb-3">
                <b-col cols="12" md="6">
                  <b-form-group
                    label="Search questions or groups..."
                    class="mb-0"
                  >
                    <b-form-input
                      v-model="filter"
                      placeholder="Search questions or groups..."
                      type="search"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Questions grouped by category -->
              <div
                v-if="groupedQuestions.length === 0"
                class="text-center py-4"
              >
                <feather-icon
                  icon="AlertCircleIcon"
                  size="48"
                  class="text-muted mb-2"
                />
                <h5 class="text-muted">No questions added yet</h5>
                <p class="text-muted">
                  Start by adding your first question above.
                </p>
              </div>

              <div
                v-for="group in groupedQuestions"
                :key="group.id"
                class="mb-4"
              >
                <b-card class="shadow-sm border-0">
                  <!-- Group Header -->
                  <template #header>
                    <div
                      class="d-flex justify-content-between align-items-center w-100"
                    >
                      <div class="d-flex align-items-center flex-grow-1">
                        <feather-icon
                          icon="FolderIcon"
                          size="20"
                          class="text-info mr-2"
                        />
                        <h5 class="mb-0 text-dark font-weight-bold">
                          {{ group.groupName }}
                        </h5>
                        <span
                          v-if="
                            group.score !== undefined && group.score !== null
                          "
                          class="ml-2 badge badge-pill badge-info"
                          >Score: {{ group.score }}</span
                        >
                        <div class="ml-2 d-flex align-items-center">
                          <b-badge variant="light-info" class="mr-2">
                            {{ group.questions.length }} question{{
                              group.questions.length !== 1 ? "s" : ""
                            }}
                          </b-badge>
                          <div
                            v-if="group.scoringQuestionsCount > 0"
                            class="d-flex align-items-center"
                          >
                            <b-badge
                              variant="light-success"
                              class="mr-1"
                              v-b-tooltip.hover
                              title="Total Max Score"
                            >
                              <feather-icon
                                icon="TrendingUpIcon"
                                size="12"
                                class="mr-1"
                              />
                              {{ group.totalMaxScore }}
                            </b-badge>
                            <b-badge
                              variant="light-primary"
                              v-b-tooltip.hover
                              title="Total Passing Score"
                            >
                              <feather-icon
                                icon="CheckCircleIcon"
                                size="12"
                                class="mr-1"
                              />
                              {{ group.totalPassingScore }}
                            </b-badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Questions in this group -->
                  <div class="question-list">
                    <div
                      v-for="(question, index) in group.questions"
                      :key="question.questionId"
                      class="question-item"
                      :class="{
                        'border-bottom': index < group.questions.length - 1,
                      }"
                    >
                      <b-row class="align-items-center py-3">
                        <!-- Question Status Badge -->
                        <b-col cols="auto">
                          <b-badge
                            :variant="
                              question.type.status === 'A'
                                ? 'light-primary'
                                : 'light-warning'
                            "
                            class="badge-glow"
                          >
                            <feather-icon
                              :icon="
                                question.type.status === 'A'
                                  ? 'CheckCircleIcon'
                                  : 'MessageSquareIcon'
                              "
                              size="12"
                            />
                            {{
                              question.type.status === "A"
                                ? "Question"
                                : "Comment"
                            }}
                          </b-badge>
                        </b-col>

                        <!-- Question Content -->
                        <b-col>
                          <div class="question-content">
                            <h6 class="mb-1 text-dark">{{ question.title }}</h6>
                            <p
                              v-if="question.subtitle"
                              class="text-muted small mb-1"
                            >
                              {{ question.subtitle }}
                            </p>
                            <div
                              v-if="question.type.status === 'A'"
                              class="d-flex align-items-center"
                            >
                              <small class="text-muted mr-3">
                                <feather-icon
                                  icon="TrendingUpIcon"
                                  size="12"
                                  class="mr-1"
                                />
                                Max Score: {{ question.maxScore }}
                              </small>
                              <small class="text-muted">
                                <feather-icon
                                  icon="CheckIcon"
                                  size="12"
                                  class="mr-1"
                                />
                                Passing Score: {{ question.passingScore }}
                              </small>
                            </div>
                          </div>
                        </b-col>

                        <!-- Actions -->
                        <b-col cols="auto">
                          <div class="d-flex">
                            <b-button
                              variant="outline-warning"
                              size="sm"
                              class="mr-1"
                              v-b-tooltip.hover
                              title="Edit Question"
                              @click="editQuestion(question)"
                            >
                              <feather-icon icon="Edit3Icon" size="16" />
                            </b-button>
                            <b-button
                              variant="outline-danger"
                              size="sm"
                              v-b-tooltip.hover
                              title="Delete Question"
                              @click="deleteQuestion(question.questionId)"
                            >
                              <feather-icon icon="Trash2Icon" size="16" />
                            </b-button>
                          </div>
                        </b-col>
                      </b-row>
                    </div>
                  </div>
                </b-card>
              </div>

              <!-- Summary info -->
              <div
                v-if="questions.length > 0"
                class="text-center text-muted mt-4"
              >
                <small>
                  Total: {{ questions.length }} question{{
                    questions.length !== 1 ? "s" : ""
                  }}
                  in {{ groupedQuestions.length }} group{{
                    groupedQuestions.length !== 1 ? "s" : ""
                  }}
                </small>
              </div>
            </b-col>
          </b-row>
        </validation-observer>
        <b-modal
          id="modal-prevent-closing"
          ref="modal"
          title="Edit Question"
          size="lg"
          v-model="modalShow"
          @hidden="resetModal"
          @ok="handleOk"
        >
          <validation-observer ref="editQuestionRules">
            <form ref="form" @submit.stop.prevent="handleSubmit">
              <b-row>
                <b-col cols="12" sm="12" md="12" xl="12">
                  <b-form-group
                    label="Question Type"
                    label-for="EditQuestionType"
                  >
                    <validation-provider
                      name="EditQuestionType"
                      #default="{ errors }"
                    >
                      <v-select
                        id="EditQuestionType"
                        v-model="editQuestionData.type"
                        :options="questionTypes"
                        :reduce="(val) => val"
                        :filterable="false"
                        label="name"
                        :clearable="false"
                        input-id="EditQuestionType"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" xl="12" md="12" sm="12">
                  <b-form-group label="Group" label-for="editGroup">
                    <validation-provider #default="{ errors }" name="editGroup">
                      <v-select
                        id="editGroup"
                        v-model="editQuestionData.group"
                        :taggable="true"
                        :autoscroll="true"
                        maxHeight="50"
                        :create-option="(temp) => ({ id: 0, groupName: temp })"
                        :loading="groupSelectBoxLoading"
                        @option:created="editGroupValue"
                        @option:selected="selectedGroupValue"
                        :reduce="(val) => val"
                        :options="groupData"
                        label="groupName"
                        :clearable="true"
                      >
                      </v-select>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" xl="12" md="12" sm="12">
                  <b-form-group label="Question" label-for="editQuestion">
                    <validation-provider
                      #default="{ errors }"
                      name="editQuestion"
                    >
                      <b-input-group>
                        <b-form-textarea
                          id="editQuestion"
                          v-model.lazy="editQuestionData.title"
                          type="text"
                          rows="3"
                          placeholder="Edit Question"
                          :state="editTitleState"
                          aria-describedby="input-live-feedback-edit"
                          no-resize
                        ></b-form-textarea>
                        <small class="text-danger">{{ errors[0] }}</small>
                        <b-form-invalid-feedback id="input-live-feedback-edit">
                          Enter at least 5 letters
                        </b-form-invalid-feedback>
                      </b-input-group>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Subtitle Explain for Edit - Only for Answer type questions -->
                <b-col cols="12" xl="12" md="12" sm="12">
                  <b-form-group
                    label="Subtitle (Optional)"
                    label-for="editSubtitle"
                  >
                    <b-form-input
                      id="editSubtitle"
                      v-model="editQuestionData.subtitle"
                      placeholder="Add explanation for this question (optional)"
                      rows="2"
                      no-resize
                    ></b-form-input>
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  xl="6"
                  md="6"
                  sm="6"
                  v-if="editQuestionData.type.status === 'A'"
                >
                  <b-form-group label="Max Score" label-for="editMaxScore">
                    <validation-provider
                      #default="{ errors }"
                      name="editMaxScore"
                    >
                      <b-form-input
                        id="editMaxScore"
                        v-model.number="editQuestionData.maxScore"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter max score (0-100)"
                        :state="editMaxScoreState"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                      <b-form-invalid-feedback>
                        Max score must be between 0-100 and greater than min
                        score
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  xl="6"
                  md="6"
                  sm="6"
                  v-if="editQuestionData.type.status === 'A'"
                >
                  <b-form-group
                    label="Passing Score"
                    label-for="editPassingScore"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="editPassingScore"
                    >
                      <b-form-input
                        id="editPassingScore"
                        v-model.number="editQuestionData.passingScore"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter passing score (0-100)"
                        :state="editPassingScoreState"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                      <b-form-invalid-feedback>
                        Passing score must be between min and max scores
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </form>
          </validation-observer>
        </b-modal>
        <!-- Group Add/Edit Modal -->
        <b-modal
          id="group-modal"
          ref="groupModal"
          v-model="groupModalShow"
          :title="groupModalEditMode ? 'Edit Group' : 'Add Group'"
          @ok="handleGroupModalOk"
          @hidden="resetGroupModal"
          ok-title="Save"
          cancel-title="Cancel"
        >
          <validation-observer ref="groupModalRules">
            <form @submit.stop.prevent="handleGroupModalOk">
              <b-form-group label="Group Name" label-for="groupModalName">
                <validation-provider
                  name="Group Name"
                  rules="required|min:2"
                  #default="{ errors }"
                >
                  <b-form-input
                    id="groupModalName"
                    v-model="groupModalData.groupName"
                    placeholder="Enter group name"
                    :state="
                      groupModalData.groupName &&
                        groupModalData.groupName.length >= 2
                    "
                  ></b-form-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Group Score (optional)"
                label-for="groupModalScore"
              >
                <b-form-input
                  id="groupModalScore"
                  v-model.number="groupModalData.score"
                  type="number"
                  min="0"
                  placeholder="Enter group score (optional)"
                ></b-form-input>
              </b-form-group>
            </form>
          </validation-observer>
        </b-modal>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BPagination,
  BFormRating,
  BIcon,
  VBTooltip,
} from "bootstrap-vue";
import settingsStoreModule from "../settingsStoreModule";
import store from "@/store";
import { ref, onUnmounted } from "@vue/composition-api";
import vSelect from "vue-select";
import router from "@/router";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import _ from "lodash";
import axiosIns from "@/libs/axios";
import { required, email } from "@validations";
import "animate.css";
import ColourPicker from "vue-colour-picker";

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BPagination,
    BIcon,
    BFormRating,
    vSelect,
    //validation
    ValidationProvider,
    ValidationObserver,
    ColourPicker,
  },

  directives: {
    "b-tooltip": VBTooltip,
  },

  setup() {
    const SETTINGS_APP_STORE_MODULE_NAME = "settings";
    // Register module
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
    });
  },
  mounted() {
    this.getGroups();
    this.loadRoles();
  },
  updated() {
    // we only want it to run once for start colour picker
    if (this.$refs.refChartColour) {
      if (this.colourCount == 0) {
        this.colourCount = 1;
        this.$refs.refChartColour.colorValue = "#009CE0";
      }
    }
  },
  data() {
    return {
      dir: "ltr",
      chartColour: "#009CE0",
      questionnaire: {
        id: 0,
        title: null,
        editableByReviewee: false,
      },
      showReviewDetail: false,
      groupSelectBoxLoading: false,
      questionData: {
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: null,
        maxScore: null,
        passingScore: null,
      },
      editQuestionData: {
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: null,
      },
      groupData: [],
      questionTypes: [
        { name: "Comment", status: "B" },
        { name: "Answer", status: "A" },
      ],
      loading: false,
      edit: null,
      newitem: "",
      filter: null,
      modalShow: false,
      questions: [],
      colourCount: 0,
      groupModalShow: false,
      groupModalEditMode: false,
      groupModalData: {
        groupName: "",
        score: null,
        id: null,
      },
      // Roles & visibility
      roles: [],
      rolesLoading: false,
      hideScoresFrom: [],
      hideCommentsFrom: [],
      visibleToRoles: [],
      // Follow-up config
      followUpConfig: { enabled: false, intervals: [] },
      followUp6Month: false,
      followUp1Year: false,
    };
  },
  computed: {
    groupedQuestions() {
      let filteredQuestions = this.questions;

      // Apply search filter
      if (this.filter && this.filter.trim()) {
        const searchTerm = this.filter.toLowerCase().trim();
        filteredQuestions = this.questions.filter(
          (question) =>
            question.title.toLowerCase().includes(searchTerm) ||
            question.group.groupName.toLowerCase().includes(searchTerm) ||
            (question.subtitle &&
              question.subtitle.toLowerCase().includes(searchTerm))
        );
      }

      // Group questions by group
      const grouped = _.groupBy(filteredQuestions, "group.id");

      return Object.keys(grouped)
        .map((groupId) => {
          const questionsInGroup = grouped[groupId];
          const firstQuestion = questionsInGroup[0];

          // Calculate sums for scoring questions only (type A)
          const scoringQuestions = questionsInGroup.filter(
            (q) => q.type.status === "A"
          );
          const totalMaxScore = scoringQuestions.reduce(
            (sum, q) => sum + (q.maxScore || 0),
            0
          );
          const totalPassingScore = scoringQuestions.reduce(
            (sum, q) => sum + (q.passingScore || 0),
            0
          );

          return {
            id: firstQuestion.group.id,
            groupName: firstQuestion.group.groupName,
            questions: _.orderBy(questionsInGroup, ["id"], ["asc"]),
            totalMaxScore,
            totalPassingScore,
            scoringQuestionsCount: scoringQuestions.length,
          };
        })
        .sort((a, b) => a.id - b.id);
    },
    questionnaireNameState() {
      if (this.questionnaire.title === null) {
        return null;
      } else {
        return this.questionnaire.title.length > 5 ? true : false;
      }
    },
    titleState() {
      if (this.questionData.title === "") {
        return null;
      } else {
        return this.questionData.title.length > 5 ? true : false;
      }
    },
    editTitleState() {
      if (this.editQuestionData.title === "") {
        return null;
      } else {
        return this.editQuestionData.title.length > 5 ? true : false;
      }
    },

    maxScoreState() {
      if (this.questionData.maxScore === null) return null;
      if (this.questionData.type.status !== "A") return null;

      const score = Number(this.questionData.maxScore);
      if (isNaN(score)) return false;
      if (score < 0 || score > 100) return false;

      return true;
    },

    passingScoreState() {
      if (this.questionData.passingScore === null) return null;
      if (this.questionData.type.status !== "A") return null;

      const score = Number(this.questionData.passingScore);
      const maxScore = Number(this.questionData.maxScore);

      if (isNaN(score)) return false;
      if (score < 0 || score > 100) return false;
      if (score > maxScore) return false;

      return true;
    },

    editMaxScoreState() {
      if (this.editQuestionData.maxScore === null) return null;
      if (this.editQuestionData.type.status !== "A") return null;

      const score = Number(this.editQuestionData.maxScore);
      if (isNaN(score)) return false;
      if (score < 0 || score > 100) return false;

      return true;
    },
    editPassingScoreState() {
      if (this.editQuestionData.passingScore === null) return null;
      if (this.editQuestionData.type.status !== "A") return null;

      const score = Number(this.editQuestionData.passingScore);
      const maxScore = Number(this.editQuestionData.maxScore);

      if (isNaN(score)) return false;
      if (score < 0 || score > 100) return false;
      if (score > maxScore) return false;

      return true;
    },
  },
  methods: {
    onFollowUpEnabledChange(val) {
      if (!val) {
        this.followUp6Month = false;
        this.followUp1Year = false;
        this.followUpConfig.intervals = [];
      }
    },
    syncFollowUpIntervals() {
      const intervals = [];
      if (this.followUp6Month) intervals.push({ months: 6, label: '6-Month Follow-up' });
      if (this.followUp1Year) intervals.push({ months: 12, label: '1-Year Follow-up' });
      this.followUpConfig.intervals = intervals;
    },
    saveButton() {
      store
        .dispatch("settings/updateQuestionnaireColor", {
          id: this.questionnaire.id,
          chartColour: this.chartColour,
        })
        .then((res) => {
          this.toastMessage("Questionnaire");
          router.push({ name: "settings-review-list" });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    createQuestionnaire() {
      if (this.questionnaire.title.length > 5) {
        store
          .dispatch("settings/createQuestionnaire", {
            title: this.questionnaire.title.trim(),
            hide_scores_from: this.hideScoresFrom,
            hide_comments_from: this.hideCommentsFrom,
            visible_to_roles: this.visibleToRoles,
            editableByReviewee: this.questionnaire.editableByReviewee,
            follow_up_config: this.followUpConfig,
          })
          .then((res) => {
            if (res.status === 201 && res.data.existing == 0) {
              this.questionnaire.id = res.data.data.id;
              this.toastMessage("New Questionnaire");
              // Automatically navigate to edit page
              router.push({
                name: "settings-edit-review",
                params: { id: this.questionnaire.id },
              });
            } else {
              this.existingToastMessage("Questionnaire");
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        this.questionnaireNameState;
      }
    },
    loadRoles() {
      this.rolesLoading = true;
      this.$http
        .get("roles")
        .then((response) => {
          const body = response && response.data ? response.data : [];
          // Support common API shapes: array, {data: [...]}, {roles: [...]}
          const items = Array.isArray(body)
            ? body
            : Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body?.roles)
            ? body.roles
            : [];
          this.roles = items;
        })
        .catch((error) => {
          console.error("Error loading roles:", error);
        })
        .finally(() => {
          this.rolesLoading = false;
        });
    },
    editReview() {
      if (this.questionnaire.title.length > 5) {
        this.loading = true;
        store
          .dispatch("settings/updateReview", {
            id: this.questionnaire.id,
            title: this.questionnaire.title.trim(),
            hide_scores_from: this.hideScoresFrom,
            hide_comments_from: this.hideCommentsFrom,
            visible_to_roles: this.visibleToRoles,
            editableByReviewee: this.questionnaire.editableByReviewee,
          })
          .then((res) => {
            if (res.status === 200 && res.data.existing == 0) {
              this.questionnaire.id = res.data.data.id;
              this.editToastMessage("Questionnaire");
              this.loading = false;
            } else {
              this.loading = false;
              this.existingToastMessage("Questionnaire");
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        this.questionnaireNameState;
      }
    },
    addQuestion() {
      this.$refs.questionaireRules2.validate().then((success) => {
        if (success) {
          if (this.questionData.type.status === "A") {
            if (!this.maxScoreState || !this.passingScoreState) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Warning",
                  text: "Please check score values",
                  icon: "AlertTriangleIcon",
                  variant: "warning",
                },
              });
              return;
            }
          }

          const newQuestion = {
            title: this.questionData.title,
            subtitle: this.questionData.subtitle,
            groupId: this.questionData.group.id,
            status: this.questionData.type.status
              ? this.questionData.type.status
              : "A",
            ...(this.questionData.type.status === "A" && {
              maxScore: this.questionData.maxScore,
              passingScore: this.questionData.passingScore,
            }),
          };

          store
            .dispatch("settings/addNewQuestionItem", newQuestion)
            .then((res) => {
              if (res.status === 201) {
                this.getGroups();
                this.clearQuestionData();
                this.toastMessage("New Question");
              }
            })
            .catch((error) => {
              console.log(error);
            });
        }
      });
    },
    createNewGroupValue(val) {
      this.groupSelectBoxLoading = true;
      if (
        this.groupData.find(
          (x) => x.groupName.toUpperCase() == val.groupName.toUpperCase()
        ) == undefined
      ) {
        store
          .dispatch("settings/addNewGroupItem", {
            groupName: val.groupName.trim(),
            surveyId: this.questionnaire.id,
          })
          .then((response) => {
            if (response.status == 201) {
              this.questionData.group = response.data;
              this.getGroups();
              this.toastMessage("New Group");
              this.groupSelectBoxLoading = false;
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        this.groupSelectBoxLoading = false;
      }
    },
    editGroupValue(val) {
      this.groupSelectBoxLoading = true;

      if (
        this.groupData.find(
          (x) => x.groupName.toUpperCase() == val.groupName.toUpperCase()
        ) == undefined
      ) {
        store
          .dispatch("settings/addNewGroupItem", {
            groupName: val.groupName.trim(),
            surveyId: this.questionnaire.id,
          })
          .then((response) => {
            if (response.status == 201) {
              //this.groupData.unshift(response.data);
              this.questionData.group = {};
              this.getGroups();
              this.toastMessage("New Group");
              this.groupSelectBoxLoading = false;
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        this.groupSelectBoxLoading = false;
      }
    },
    editQuestion(question) {
      this.modalShow = true;
      this.editQuestionData = {
        id: question.questionId,
        title: question.title,
        subtitle: question.subtitle,
        group: question.group,
        type:
          question.type.status === "A"
            ? { name: "Answer", status: "A" }
            : { name: "Comment", status: "B" },
        maxScore: question.maxScore,
        passingScore: question.passingScore,
      };
    },
    getGroups() {
      this.groupSelectBoxLoading = true;
      store
        .dispatch("settings/fetchGroups", [])
        .then((res) => {
          this.questions = [];
          this.groupData = [];
          res.data.forEach((group) => {
            if (group.surveyId === this.questionnaire.id) {
              this.groupData.unshift({
                id: group.id,
                groupName: group.groupName,
                status: group.status,
                score: group.score,
              });
              if (group != null && group.questions.length > 0) {
                group.questions.forEach((data) => {
                  this.questions.push({
                    id: data.id,
                    questionId: data.id,
                    type: { status: data.status },
                    group: { id: group.id, groupName: group.groupName },
                    title: data.title,
                    subtitle: data.subtitle,
                    maxScore: data.maxScore,
                    passingScore: data.passingScore,
                  });
                });
              }
            }
          });
          this.groupSelectBoxLoading = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    selectedGroupValue(val) {
      //   console.log(val);
    },
    deleteQuestion(id) {
      const self = this;
      this.$swal({
        title: "Are you sure you want to delete the question?",
        // text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.isConfirmed) {
          store
            .dispatch("settings/deleteQuestion", id)
            .then((res) => {
              self.getGroups();
              self.$swal.fire(
                "Deleted!",
                "Question has been deleted.",
                "success"
              );
            })
            .catch((error) => {
              console.log(error);
            });
        }
      });
    },
    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.$refs.editQuestionRules.validate().then((success) => {
        if (success) {
          if (this.editQuestionData.title !== "") {
            this.handleSubmit();
          } else {
          }
        }
      });
    },
    handleSubmit() {
      const editQuestionData = {
        id: this.editQuestionData.id,
        title: this.editQuestionData.title,
        subtitle: this.editQuestionData.subtitle,
        groupId: this.editQuestionData.group.id,
        status: this.editQuestionData.type.status,
        ...(this.editQuestionData.type.status === "A" && {
          maxScore: this.editQuestionData.maxScore,
          passingScore: this.editQuestionData.passingScore,
        }),
      };
      store
        .dispatch("settings/editQuestion", editQuestionData)
        .then((res) => {
          this.getGroups();
          this.resetModal();
          this.modalShow = false;
          this.editToastMessage("Question");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    resetModal() {
      this.editQuestionData = {
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: {},
      };
    },
    toastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Successful",
          text: "✔️ " + val + " Added Successful",
          icon: "ThumbsUpIcon",
          variant: "success",
        },
      });
    },
    editToastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Successful",
          text: "✔️ " + val + " Updated Successful",
          icon: "CheckIcon",
          variant: "success",
        },
      });
    },
    existingToastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Warning",
          text: "This " + val + " name is existing",
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
      });
    },
    selectGroupToastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Warning",
          text: "Please select group",
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
      });
    },

    clearQuestionData() {
      this.questionData = {
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: this.questionData.group,
        maxScore: null,
        passingScore: null,
      };
    },
    openAddGroupModal(search) {
      this.groupModalData = {
        groupName: search || "",
        score: null,
        id: null,
      };
      this.groupModalEditMode = false;
      this.groupModalShow = true;
    },
    handleGroupModalOk() {
      this.$refs.groupModalRules.validate().then((success) => {
        if (success) {
          if (this.groupModalData.groupName) {
            if (this.groupModalEditMode && this.groupModalData.id) {
              // Edit existing group
              store
                .dispatch("settings/editGroupItem", {
                  id: this.groupModalData.id,
                  groupName: this.groupModalData.groupName.trim(),
                  score: this.groupModalData.score,
                  surveyId: this.questionnaire.id,
                })
                .then((res) => {
                  if (res.status === 200) {
                    this.getGroups();
                    this.toastMessage("Group Updated");
                    this.groupModalShow = false;
                  }
                })
                .catch((error) => {
                  console.log(error);
                });
            } else {
              // Add new group
              store
                .dispatch("settings/addNewGroupItem", {
                  groupName: this.groupModalData.groupName.trim(),
                  score: this.groupModalData.score,
                  surveyId: this.questionnaire.id,
                })
                .then((res) => {
                  if (res.status === 201) {
                    // Automatically select the newly created group
                    this.questionData.group = res.data;
                    this.getGroups();
                    this.toastMessage("New Group");
                    this.groupModalShow = false;
                  }
                })
                .catch((error) => {
                  console.log(error);
                });
            }
          }
        }
      });
    },
    resetGroupModal() {
      this.groupModalData = {
        groupName: "",
        score: null,
        id: null,
      };
      this.groupModalEditMode = false;
    },
    openEditGroupModal(group) {
      this.groupModalData = {
        groupName: group.groupName,
        score: group.score,
        id: group.id,
      };
      this.groupModalEditMode = true;
      this.groupModalShow = true;
    },
    deleteGroup(group) {
      const self = this;
      this.$swal({
        title: "Are you sure you want to delete the group?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.isConfirmed) {
          store
            .dispatch("settings/deleteGroup", { id: group.id })
            .then((res) => {
              self.getGroups();
              this.questionData.group = null;
              self.$swal.fire("Deleted!", "Group has been deleted.", "success");
            })
            .catch((error) => {
              console.log(error);
            });
        }
      });
    },
    canDeleteGroup(group) {
      // Only allow delete if group has no questions
      const groupQuestions = this.questions.filter(
        (q) => q.group.id === group.id
      );
      return groupQuestions.length === 0;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";

/* Welcome Card Styling */
.welcome-card {
  background: linear-gradient(135deg, #667eea 0%, #64cee9 100%);
  color: white;

  .text-primary {
    color: white !important;
  }

  .text-muted {
    color: rgba(255, 255, 255, 0.8) !important;
  }

  .form-control {
    border: 2px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.1);
    color: white;

    &::placeholder {
      color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
      border-color: white;
      background: rgba(255, 255, 255, 0.2);
      box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.25);
    }
  }

  .input-group-text {
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: white;
  }

  .btn-primary {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid white;
    color: white;

    &:hover {
      background: white;
      color: #667eea;
      transform: translateY(-2px);
    }

    &:disabled {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
      color: rgba(255, 255, 255, 0.5);
    }
  }
}

/* Card-based design styles */
.question-item {
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(46, 183, 224, 0.05);
    border-radius: 0.25rem;
  }

  .question-content {
    h6 {
      line-height: 1.4;
      margin-bottom: 0.25rem;
    }

    p {
      line-height: 1.3;
      margin-bottom: 0.5rem;
    }
  }
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.badge-glow {
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

/* Card header styling */
.card-header {
  background: linear-gradient(135deg, #a7a7a7 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 0.375rem 0.375rem 0 0 !important;

  h5 {
    color: #495057 !important;
  }

  .badge {
    &.badge-light-info {
      background-color: rgba(23, 162, 184, 0.1) !important;
      color: #17a2b8 !important;
      border: 1px solid rgba(23, 162, 184, 0.2);
    }

    &.badge-light-success {
      background-color: rgba(40, 167, 69, 0.1) !important;
      color: #28a745 !important;
      border: 1px solid rgba(40, 167, 69, 0.2);
    }

    &.badge-light-primary {
      background-color: rgba(0, 123, 255, 0.1) !important;
      color: #007bff !important;
      border: 1px solid rgba(0, 123, 255, 0.2);
    }
  }
}

/* Improve spacing and visual hierarchy */
.question-list {
  .question-item:last-child {
    .border-bottom {
      border-bottom: none !important;
    }
  }
}

/* Empty state styling */
.text-center {
  .feather-icon {
    opacity: 0.5;
  }
}

.color-input {
  width: 80% !important;
  margin-right: 2px !important;
  height: 38px;
  border: 1px solid #d8d6de;
}
.current-color {
  height: 38px !important;
}

/* Color picker palette positioning fix */
.colour-picker {
  position: relative !important;
}

.colour-picker .compact-picker {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  z-index: 9999 !important;
  margin-top: 5px !important;
}

.compact-picker {
  position: absolute !important;
  z-index: 9999 !important;
}
</style>
